/**
 * Saved Views Dropdown
 *
 * A dropdown component for managing saved filter views.
 * Shows saved views, allows creating new ones, and applying them.
 */

import { useState } from 'react';
import {
  Dropdown,
  Button,
  Space,
  Typography,
  Input,
  Divider,
  Empty,
  Spin,
  message,
  Popconfirm,
  Modal,
  Form,
} from 'antd';
import {
  DownOutlined,
  StarOutlined,
  StarFilled,
  DeleteOutlined,
  PlusOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { savedViewsApi, type SavedView, type CreateSavedView } from '@api/saved-views';

const { Text } = Typography;

interface SavedViewsDropdownProps {
  viewType: 'tasks' | 'templates' | 'kanban' | 'calendar' | 'search';
  currentFilters: Record<string, unknown>;
  currentSort?: Record<string, unknown>;
  onApplyView: (filters: Record<string, unknown>, sort?: Record<string, unknown> | null) => void;
}

export default function SavedViewsDropdown({
  viewType,
  currentFilters,
  currentSort,
  onApplyView,
}: SavedViewsDropdownProps) {
  const queryClient = useQueryClient();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Fetch saved views
  const { data: views = [], isLoading } = useQuery({
    queryKey: ['saved-views', viewType],
    queryFn: () => savedViewsApi.list(),
    select: (data) => data.filter((v) => v.viewType === viewType),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateSavedView) => savedViewsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-views'] });
      message.success('View saved');
      setSaveModalOpen(false);
      form.resetFields();
    },
    onError: () => {
      message.error('Failed to save view');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => savedViewsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-views'] });
      message.success('View deleted');
    },
    onError: () => {
      message.error('Failed to delete view');
    },
  });

  // Toggle pin mutation
  const togglePinMutation = useMutation({
    mutationFn: (id: string) => savedViewsApi.togglePin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-views'] });
    },
    onError: () => {
      message.error('Failed to update pin status');
    },
  });

  const handleSaveView = () => {
    form.validateFields().then((values) => {
      createMutation.mutate({
        name: values.name,
        view_type: viewType,
        filter_config: currentFilters,
        sort_config: currentSort,
        is_pinned: false,
      });
    });
  };

  const handleApplyView = (view: SavedView) => {
    onApplyView(view.filterConfig, view.sortConfig);
  };

  const pinnedViews = views.filter((v) => v.isPinned);
  const unpinnedViews = views.filter((v) => !v.isPinned);

  const dropdownContent = (
    <div style={{ minWidth: 240, padding: 8 }}>
      {isLoading ? (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      ) : views.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No saved views"
          style={{ margin: '16px 0' }}
        />
      ) : (
        <>
          {/* Pinned views */}
          {pinnedViews.length > 0 && (
            <>
              <Text type="secondary" style={{ fontSize: 11, padding: '0 8px' }}>
                PINNED
              </Text>
              {pinnedViews.map((view) => (
                <ViewItem
                  key={view.id}
                  view={view}
                  onApply={() => handleApplyView(view)}
                  onTogglePin={() => togglePinMutation.mutate(view.id)}
                  onDelete={() => deleteMutation.mutate(view.id)}
                />
              ))}
              <Divider style={{ margin: '8px 0' }} />
            </>
          )}

          {/* Unpinned views */}
          {unpinnedViews.length > 0 && (
            <>
              {pinnedViews.length > 0 && (
                <Text type="secondary" style={{ fontSize: 11, padding: '0 8px' }}>
                  ALL VIEWS
                </Text>
              )}
              {unpinnedViews.map((view) => (
                <ViewItem
                  key={view.id}
                  view={view}
                  onApply={() => handleApplyView(view)}
                  onTogglePin={() => togglePinMutation.mutate(view.id)}
                  onDelete={() => deleteMutation.mutate(view.id)}
                />
              ))}
            </>
          )}
        </>
      )}

      <Divider style={{ margin: '8px 0' }} />

      {/* Save current view button */}
      <Button
        type="text"
        icon={<PlusOutlined />}
        block
        style={{ textAlign: 'left' }}
        onClick={() => setSaveModalOpen(true)}
      >
        Save current view
      </Button>
    </div>
  );

  return (
    <>
      <Dropdown
        dropdownRender={() => dropdownContent}
        trigger={['click']}
        placement="bottomLeft"
      >
        <Button icon={<FilterOutlined />}>
          <Space>
            Views
            <DownOutlined style={{ fontSize: 10 }} />
          </Space>
        </Button>
      </Dropdown>

      {/* Save Modal */}
      <Modal
        title="Save Current View"
        open={saveModalOpen}
        onCancel={() => setSaveModalOpen(false)}
        onOk={handleSaveView}
        confirmLoading={createMutation.isPending}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="View Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g., My pending tasks" autoFocus />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Current filters will be saved with this view.
          </Text>
        </Form>
      </Modal>
    </>
  );
}

// Individual view item component
interface ViewItemProps {
  view: SavedView;
  onApply: () => void;
  onTogglePin: () => void;
  onDelete: () => void;
}

function ViewItem({ view, onApply, onTogglePin, onDelete }: ViewItemProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '6px 8px',
        borderRadius: 4,
        cursor: 'pointer',
      }}
      className="saved-view-item"
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f5f5f5';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <div style={{ flex: 1 }} onClick={onApply}>
        <Text style={{ fontSize: 13 }}>{view.name}</Text>
      </div>
      <Space size={4}>
        <Button
          type="text"
          size="small"
          icon={view.isPinned ? <StarFilled style={{ color: 'var(--status-warning)' }} /> : <StarOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
        />
        <Popconfirm
          title="Delete this view?"
          onConfirm={(e) => {
            e?.stopPropagation();
            onDelete();
          }}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      </Space>
    </div>
  );
}
